import { useTranslation } from "react-i18next";
import { Pill } from "../../components";
import { formatDate, formatDateTime } from "../../utils";
import { DeliveryInterface, OfferResponse } from "../../types";
import DeliveryOffersTable from "./DeliveryOffersTable";

export interface DeliveryInfoPanelProps {
  delivery: DeliveryInterface;
  isSeller: boolean;
  selectedOffer?: OfferResponse;
  selectedOffers: OfferResponse[];
  total: number;
}

/**
 * The main read panel of the delivery detail — heading (`{orderName} - PO {number}`),
 * the `DeliveryState` status `Pill`, the `dl` of dates/place/receiver/comments, and the
 * role-swapped party block (`isSeller` shows the BUYER, buyer shows the SUPPLIER). Purely
 * presentational and identical for both roles except that swap; no action controls live
 * here, keeping the seller view read-only.
 */
const DeliveryInfoPanel = ({
  delivery,
  isSeller,
  selectedOffer,
  selectedOffers,
  total,
}: DeliveryInfoPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className="delivery-panel">
      <div className="delivery-panel__header">
        <h2 className="delivery-panel__title">
          {delivery.orderName} - {t("deliveries.detail.purchaseOrder")}{" "}
          {delivery.purchaseOrderNumber}
        </h2>
        <Pill delivery={delivery} />
      </div>

      <dl className="delivery-panel__grid">
        <div className="delivery-panel__block">
          <dt>{t("deliveries.detail.deliveryDateTime")}</dt>
          <dd>
            {delivery.deliveryDate && (
              <time dateTime={formatDate(delivery.deliveryDate)}>
                {formatDate(delivery.deliveryDate)} - {delivery.deliveryTime}
              </time>
            )}
          </dd>
        </div>

        <div className="delivery-panel__block">
          <dt>{t("deliveries.detail.createdAt")}</dt>
          <dd>
            <time dateTime={formatDateTime(delivery.createdAt)}>
              {formatDateTime(delivery.createdAt)}
            </time>
          </dd>
        </div>

        <div className="delivery-panel__block delivery-panel__block--bordered">
          <dt className="delivery-panel__group-label">
            {t("deliveries.detail.deliveryPlace")}
          </dt>
          <dd className="delivery-panel__group-value">
            {delivery.site?.name}
            <br />
            {delivery.site?.address}
          </dd>

          <dt className="delivery-panel__group-label">
            {t("deliveries.detail.receives")}
          </dt>
          <dd className="delivery-panel__group-value">
            {delivery.receiverEmail}
          </dd>

          {delivery.receiverComment && (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.form.receiverComment")}
              </dt>
              <dd className="delivery-panel__group-value">
                {delivery.receiverComment}
              </dd>
            </>
          )}

          {delivery.comment && (
            <>
              <dt className="delivery-panel__group-label">
                {t("tenders.form.comments")}
              </dt>
              <dd className="delivery-panel__group-value">
                {delivery.comment}
              </dd>
            </>
          )}

          {delivery.receptionDate && (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.form.receptionDate")}
              </dt>
              <dd className="delivery-panel__group-value">
                {formatDate(delivery.receptionDate)}
              </dd>
            </>
          )}

          {delivery.remittanceNumber && (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.form.remittanceNumber")}
              </dt>
              <dd className="delivery-panel__group-value">
                {delivery.remittanceNumber}
              </dd>
            </>
          )}

          {delivery.remittanceFile && (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.form.remittanceFile")}
              </dt>
              <dd className="delivery-panel__group-value">
                <a
                  href={delivery.remittanceFile}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("deliveries.form.remittanceFileView")}
                </a>
              </dd>
            </>
          )}
        </div>

        <div className="delivery-panel__block delivery-panel__block--bordered">
          {isSeller ? (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.detail.buyer")}
              </dt>
              <dd className="delivery-panel__group-value">
                {delivery.company?.name} - {delivery.user?.fullName}
                <span>{delivery.user?.email}</span>
              </dd>
            </>
          ) : (
            <>
              <dt className="delivery-panel__group-label">
                {t("deliveries.detail.supplier")}
              </dt>
              <dd className="delivery-panel__group-value">
                {delivery.supplier && (
                  <>
                    {delivery.supplier?.company?.name} -{" "}
                    {delivery.supplier?.fullName}
                  </>
                )}
                {delivery.supplierEmails?.map((email) => (
                  <span key={email}>{email}</span>
                ))}
              </dd>
            </>
          )}
        </div>
      </dl>

      <DeliveryOffersTable
        delivery={delivery}
        selectedOffer={selectedOffer}
        selectedOffers={selectedOffers}
        total={total}
      />
    </div>
  );
};

export default DeliveryInfoPanel;
